// src/context/AuthProvider.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Normalize the env value once so endpoint joins do not produce `//login`.
const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'
).replace(/\/+$/, '');
const SESSION_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const AUTH_KEYS = ['token', 'email', 'role', 'userId', 'clientId', 'user_name', 'abbreviation'];

const getStoredValue = (key) => {
  const fromLocal = localStorage.getItem(key);
  if (fromLocal !== null) return fromLocal;
  return sessionStorage.getItem(key);
};

const setStoredValue = (key, value) => {
  const normalized = value ?? '';
  localStorage.setItem(key, normalized);
  sessionStorage.setItem(key, normalized);
};

const syncSessionFromLocal = () => {
  AUTH_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) {
      sessionStorage.setItem(key, value);
    }
  });
};

const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const getPersistedUserSnapshot = () => ({
  email: getStoredValue('email'),
  role: getStoredValue('role'),
  userId: getStoredValue('userId'),
  clientId: getStoredValue('clientId'),
  name: getStoredValue('user_name'),
  abbreviation: getStoredValue('abbreviation'),
});

export const AuthProvider = ({ children }) => {
  syncSessionFromLocal();
  const bootToken = getStoredValue('token');

  if (bootToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${bootToken}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }

  const [accessToken, setAccessToken] = useState(() => bootToken);

  // Auth ready flag (IMPORTANT)
  const [authReady, setAuthReady] = useState(false);

  // User metadata (persisted in localStorage + sessionStorage)
  const [user, setUser] = useState(() => {
    const email = getStoredValue('email');
    if (!email) return null;

    return {
      token: getStoredValue('token'),
      email,
      role: getStoredValue('role'),
      userId: getStoredValue('userId'),
      clientId: getStoredValue('clientId'),
      name: getStoredValue('user_name'),
      abbreviation: getStoredValue('abbreviation'),
    };
  });

  // Axios should send cookies
  axios.defaults.withCredentials = true;

  // Attach Authorization header when accessToken changes
  useEffect(() => {
    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [accessToken]);

  // ================================
  // Refresh token on app start
  // ================================
  useEffect(() => {
    let mounted = true;

    async function tryRefreshOnStart() {
  const token = getStoredValue('token');
  const persistedEmail = getStoredValue('email');

  // Keep current session usable while refresh is being attempted
  if (token && mounted) {
    setAccessToken(token);
  }

  let isInactiveUser = false;

  try {
    const resp = await fetch(`${API_BASE}/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (resp.ok) {
      const data = await resp.json();

      // 🚨 CRITICAL FIX: Block inactive user
      if (data.data && data.data.status === 0) {
        isInactiveUser = true;

        clearAuthStorage();
        setUser(null);
        setAccessToken(null);

        console.warn("Inactive user blocked during refresh");
        return;
      }

      if (mounted && data.access_token) {
        setAccessToken(data.access_token);
        setStoredValue('token', data.access_token);

        if (data.data) {
          const persisted = getPersistedUserSnapshot();

          const nextUser = {
            email: persisted.email || data.data.email || '',
            role: persisted.role || data.data.role || '',
            userId: persisted.userId || data.data.user_id || '',
            clientId: persisted.clientId || data.data.client_id || '',
            name: persisted.name || data.data.user_name || '',
            abbreviation: persisted.abbreviation || data.data.abbreviation || '',
          };

          setStoredValue('email', nextUser.email);
          setStoredValue('role', nextUser.role);
          setStoredValue('userId', nextUser.userId);
          setStoredValue('clientId', nextUser.clientId);
          setStoredValue('user_name', nextUser.name);
          setStoredValue('abbreviation', nextUser.abbreviation);

          setUser(nextUser);
        }
      }
    }
  } catch (err) {
    console.error('Refresh on start failed:', err);
  } finally {
    // ❌ DO NOT restore session if user is inactive
    if (!isInactiveUser && mounted && persistedEmail) {
      setUser({
        token: getStoredValue('token'),
        email: persistedEmail,
        role: getStoredValue('role'),
        userId: getStoredValue('userId'),
        clientId: getStoredValue('clientId'),
        name: getStoredValue('user_name'),
        abbreviation: getStoredValue('abbreviation'),
      });
    }

    if (mounted) setAuthReady(true);
  }
}

    tryRefreshOnStart();
    return () => {
      mounted = false;
    };
  }, []);

  // ================================
  // Login
  // ================================
  async function login(email, password) {
    const resp = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.message || 'Login failed');
    }

    const data = await resp.json();

    if (!data.access_token) {
      throw new Error('No access token returned from login');
    }

    setAccessToken(data.access_token);

    if (data.data) {
      const {
        user_id,
        username,
        user_name,
        client_id,
        role,
        abbreviation,
      } = data.data;

      setStoredValue('token', data.access_token);
      setStoredValue('email', username || '');
      setStoredValue('role', role || '');
      setStoredValue('userId', user_id || '');
      setStoredValue('clientId', client_id || '');
      setStoredValue('user_name', user_name || '');
      setStoredValue('abbreviation', abbreviation || '');

      setUser({
        token: data.access_token,
        email: username,
        role,
        userId: user_id,
        clientId: client_id,
        name: user_name,
        abbreviation,
      });
    }

    return data;
  }

  // ================================
  // Refresh access token (used by axios)
  // ================================
 async function refreshAccessToken() {
  const resp = await fetch(`${API_BASE}/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!resp.ok) throw new Error('Refresh failed');

  const data = await resp.json();

  // 🚨 CRITICAL FIX: Block inactive user
  if (data.data && data.data.status === 0) {
    console.warn("Inactive user detected during token refresh");

    clearAuthStorage();
    setUser(null);
    setAccessToken(null);

    throw new Error("User inactive");
  }

  if (!data.access_token) throw new Error('No access token returned');

  setAccessToken(data.access_token);
  setStoredValue('token', data.access_token);

  if (data.data) {
    const persisted = getPersistedUserSnapshot();

    const nextUser = {
      email: persisted.email || data.data.email || '',
      role: persisted.role || data.data.role || '',
      userId: persisted.userId || data.data.user_id || '',
      clientId: persisted.clientId || data.data.client_id || '',
      name: persisted.name || data.data.user_name || '',
      abbreviation: persisted.abbreviation || data.data.abbreviation || '',
    };

    setStoredValue('email', nextUser.email);
    setStoredValue('role', nextUser.role);
    setStoredValue('userId', nextUser.userId);
    setStoredValue('clientId', nextUser.clientId);
    setStoredValue('user_name', nextUser.name);
    setStoredValue('abbreviation', nextUser.abbreviation);

    setUser(nextUser);
  }

  return data.access_token;
}

  // Keep the browser session warm so persisted login does not feel like a logout on idle.
  useEffect(() => {
    if (!user?.email || !getStoredValue('token')) return undefined;

    let cancelled = false;

    const refreshSession = async () => {
      try {
        await refreshAccessToken();
      } catch (err) {
        if (!cancelled) {
          console.warn('Background session refresh failed.', err);
        }
      }
    };

    const intervalId = window.setInterval(refreshSession, SESSION_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  // ================================
  // Logout
  // ================================
  async function logout() {
    try {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: accessToken ? `Bearer ${accessToken}` : '',
        },
      });
    } catch (err) {
      console.warn('Logout request failed', err);
    } finally {
      setAccessToken(null);
      clearAuthStorage();
      setUser(null);
      delete axios.defaults.headers.common.Authorization;
    }
  }

  // ================================
  // Axios 401 interceptor
  // ================================
  useEffect(() => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token)));
      failedQueue = [];
    };

    const interceptor = axios.interceptors.response.use(
      res => res,
      async error => {
        const originalRequest = error.config;

        if (!error.response || error.response.status !== 401) {
          return Promise.reject(error);
        }

        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return axios(originalRequest);
        } catch (err) {
          processQueue(err, null);
          // Keep persisted auth state intact. The user should only be logged out
          // through the explicit logout action, not because refresh failed after idle time.
          console.warn('Token refresh failed; preserving local auth state until manual logout.', err);
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []); // ❌ Removed `accessToken` dependency. Changing dependencies resets the interceptor during refresh!

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        accessToken,
        refreshAccessToken,
        authReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);